// src/components/StrategicSignalsTab.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { apiFetch } from '../lib/api';
import './StrategicSignalsTab.css';

const SIGNAL_TYPES = ['Exec', 'Financial', 'AI', 'Data'];

const TYPE_CONFIG = {
  Exec:      { color: 'var(--dm-indigo)', bg: '#EEF2FF', icon: '👤', label: 'Executive' },
  Financial: { color: 'var(--dm-red)', bg: 'var(--dm-red-light)', icon: '💰', label: 'Financial' },
  AI:        { color: 'var(--dm-purple-vivid)', bg: 'var(--dm-purple-pale)', icon: '🤖', label: 'AI / Tech' },
  Data:      { color: 'var(--dm-cyan)', bg: '#E0F2FE', icon: '📊', label: 'Data' },
};

const STRENGTH_CONFIG = {
  Strong:   { color: 'var(--dm-green)', bg: 'var(--dm-green-light)', icon: '🔥', label: 'Strong' },
  Moderate: { color: 'var(--dm-amber)', bg: 'var(--dm-amber-light)', icon: '⚡', label: 'Moderate' },
  Weak:     { color: 'var(--dm-grey-600)', bg: '#F3F4F6', icon: '💧', label: 'Weak' },
};

const SOURCE_ICONS = {
  Zoominfo: '🔍', '10Ks': '📄', News: '📰', RFP: '📋',
  LinkedIn: '💼', Earnings: '📈', 'Annual Report': '📑',
  Meetings: '🤝', 'Tradeshow/Conference': '🎤',
};

const OPP_INDICATOR_CONFIG = {
  Integration: { color: 'var(--dm-indigo)', bg: '#EEF2FF' },
  Core:        { color: 'var(--dm-grey-800)', bg: '#F3F4F6' },
  AI:          { color: 'var(--dm-purple-vivid)', bg: 'var(--dm-purple-pale)' },
  'Data Gov':  { color: 'var(--dm-cyan)', bg: '#E0F2FE' },
};

const EMPTY_FORM = {
  name: '',
  signalType: '',
  signalStrength: 'Strong',
  source: '',
  sourceLink: '',
  signalDate: '',        // origin date — when the event occurred
  aiInterpretation: '',
  opportunityIndicators: [],
};

function StrategicSignalsTab({ accountId, accountName = 'Account' }) {
  // Tracks which signals have been marked as "led to conversation" this session
  const [convertedSignals, setConvertedSignals] = React.useState(() => {
    try { return JSON.parse(localStorage.getItem(`converted_signals_${accountId}`) || '{}'); }
    catch { return {}; }
  });

  const markConverted = async (signalId, signalName) => {
    const updated = { ...convertedSignals, [signalId]: new Date().toISOString() };
    setConvertedSignals(updated);
    localStorage.setItem(`converted_signals_${accountId}`, JSON.stringify(updated));
    // Log to Selma feedback endpoint
    try {
      await apiFetch('/api/selma/log-signal-outcome', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ signalId, signalName, accountId, accountName, outcome: 'Led to Conversation' }),
      });
    } catch (e) { console.warn('Signal outcome log failed', e); }
  };

  const [signals, setSignals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedSignal, setSelectedSignal] = useState(null);
  const [modalMode, setModalMode] = useState('view');
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [addAnother, setAddAnother] = useState(false);
  const [filterType, setFilterType] = useState('All');

  const fetchSignals = useCallback(async () => {
    try {
      setLoading(true);
      const res = await apiFetch(`/api/signals/by-account?accountId=${accountId}`);
      const data = await res.json();
      if (data.success) {
        setSignals(data.signals);
      } else {
        setError(data.error);
      }
    } catch (err) {
      setError('Failed to load signals');
      console.error('Signals error:', err);
    } finally {
      setLoading(false);
    }
  }, [accountId]);

  useEffect(() => { fetchSignals(); }, [fetchSignals]);

  const showToast = (msg) => {
    setSuccessMessage(msg);
    setTimeout(() => setSuccessMessage(''), 3000);
  };

  const handleCardClick = (signal) => {
    setSelectedSignal(signal);
    setModalMode('view');
  };

  const handleAddClick = () => {
    setSelectedSignal(null);
    setFormData(EMPTY_FORM);
    setModalMode('add');
  };

  const handleEditClick = (signal) => {
    setFormData({
      name: signal.name || '',
      signalType: signal.signalType || '',
      signalStrength: signal.signalStrength || 'Strong',
      source: signal.source || '',
      sourceLink: signal.sourceLink || '',
      signalDate: (signal.signalDate || signal.dateObserved || '').slice(0, 10),
      aiInterpretation: signal.aiInterpretation || '',
      opportunityIndicators: signal.opportunityIndicators || [],
    });
    setModalMode('edit');
  };

  const handleCloseModal = () => {
    setSelectedSignal(null);
    setModalMode('view');
    setFormData(EMPTY_FORM);
    setSuccessMessage('');
    setShowDeleteConfirm(false);
  };

  const handleFormChange = (field, value) => {
    setFormData(prev => {
      const next = { ...prev, [field]: value };
      // In-person sources carry their own validation weight (overridable):
      // a direct meeting is the strongest signal; a tradeshow/conference one tier down.
      if (field === 'source') {
        if (value === 'Meetings') next.signalStrength = 'Strong';
        else if (value === 'Tradeshow/Conference') next.signalStrength = 'Moderate';
      }
      return next;
    });
  };

  const handleIndicatorToggle = (indicator) => {
    setFormData(prev => {
      const current = prev.opportunityIndicators || [];
      return {
        ...prev,
        opportunityIndicators: current.includes(indicator)
          ? current.filter(i => i !== indicator)
          : [...current, indicator],
      };
    });
  };

  const handleSave = async () => {
    if (!formData.name.trim()) return;
    setSaving(true);
    try {
      if (modalMode === 'add') {
        const res = await apiFetch('/api/signals/create', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            accountId,
            signal: {
              title:            formData.name,
              signal_type:      formData.signalType,
              signal_strength:  formData.signalStrength,
              source:           formData.source,
              source_link:      formData.sourceLink,
              signal_date:      formData.signalDate,      // origin date — when event occurred
              ai_interpretation: formData.aiInterpretation,
            },
          }),
        });
        const data = await res.json();
        if (data.success) {
          await fetchSignals();
          showToast(`✅ Signal added!`);
          if (addAnother) {
            setFormData(EMPTY_FORM);
          } else {
            handleCloseModal();
          }
        } else {
          alert('Failed to create: ' + data.error);
        }
      } else if (modalMode === 'edit') {
        const res = await apiFetch('/api/signals/update', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            id:                selectedSignal.id,
            title:             formData.name,
            signal_type:       formData.signalType,
            signal_strength:   formData.signalStrength,
            source:            formData.source,
            source_link:       formData.sourceLink,
            ai_interpretation: formData.aiInterpretation,
            observed_at:       formData.signalDate,
          }),
        });
        const data = await res.json();
        if (data.success) {
          await fetchSignals();
          showToast(`✅ Signal updated!`);
          handleCloseModal();
        } else {
          alert('Failed to update: ' + data.error);
        }
      }
    } catch (err) {
      alert('Error saving signal');
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedSignal) return;
    setDeleting(true);
    try {
      const res = await apiFetch(`/api/signals/delete?id=${selectedSignal.id}`, { method: 'DELETE' });
      const data = await res.json();
      if (data.success) {
        await fetchSignals();
        showToast(`🗑️ Signal deleted`);
        handleCloseModal();
      } else {
        alert('Failed to delete: ' + data.error);
      }
    } catch (err) {
      alert('Error deleting signal');
    } finally {
      setDeleting(false);
    }
  };

  // Filter signals
  const filteredSignals = filterType === 'All'
    ? signals
    : signals.filter(s => s.signalType === filterType);

  const strongCount = signals.filter(s => s.signalStrength === 'Strong').length;

  if (loading) return <div className="sig-loading"><div className="spinner" /><p>Loading signals...</p></div>;
  if (error)   return <div className="sig-error"><p>⚠️ {error}</p></div>;

  return (
    <div className="sig-container">

      {successMessage && <div className="sig-toast">{successMessage}</div>}

      {/* Header */}
      <div className="sig-header">
        <div className="sig-header-left">
          <h3>Strategic Signals <span className="sig-count">({signals.length})</span></h3>
          {signals.length > 0 && (
            <div className="sig-summary-pills">
              {strongCount > 0 && (
                <span className="sig-pill sig-pill-strong">🔥 {strongCount} Strong</span>
              )}
            </div>
          )}
        </div>
        <button className="btn-add-sig" onClick={handleAddClick}>+ Add Signal</button>
      </div>

      {/* Filter Tabs */}
      {signals.length > 0 && (
        <div className="sig-filters">
          {['All', ...SIGNAL_TYPES].map(type => {
            const count = type === 'All' ? signals.length : signals.filter(s => s.signalType === type).length;
            const cfg = TYPE_CONFIG[type];
            return (
              <button
                key={type}
                className={`sig-filter-btn ${filterType === type ? 'active' : ''}`}
                style={filterType === type && cfg ? { background: cfg.bg, color: cfg.color, borderColor: cfg.color } : {}}
                onClick={() => setFilterType(type)}
              >
                {cfg ? `${cfg.icon} ${cfg.label}` : 'All'} {count > 0 && <span className="sig-filter-count">{count}</span>}
              </button>
            );
          })}
        </div>
      )}

      {/* Signals Feed */}
      {filteredSignals.length === 0 ? (
        <div className="sig-empty">
          <p>{signals.length === 0 ? 'No strategic signals yet for this account.' : `No ${filterType} signals found.`}</p>
          {signals.length === 0 && (
            <button className="btn-add-sig-large" onClick={handleAddClick}>+ Add First Signal</button>
          )}
        </div>
      ) : (
        <div className="sig-feed">
          {filteredSignals.map(signal => {
            const typeCfg     = TYPE_CONFIG[signal.signalType] || { color: 'var(--dm-grey-600)', bg: '#F3F4F6', icon: '📋', label: signal.signalType || 'Unknown' };
            const strengthCfg = STRENGTH_CONFIG[signal.signalStrength] || { color: 'var(--dm-grey-600)', bg: '#F3F4F6', icon: '📋' };

            return (
              <div
                key={signal.id}
                className="sig-card"
                style={{ borderLeftColor: typeCfg.color }}
                onClick={() => handleCardClick(signal)}
              >
                {/* Card Header */}
                <div className="sig-card-header">
                  <div className="sig-card-type" style={{ background: typeCfg.bg, color: typeCfg.color }}>
                    {typeCfg.icon} {typeCfg.label}
                  </div>
                  <div className="sig-card-strength" style={{ background: strengthCfg.bg, color: strengthCfg.color }}>
                    {strengthCfg.icon} {signal.signalStrength}
                  </div>
                </div>

                {/* Signal Name */}
                <div className="sig-card-name">{signal.name}</div>

                {/* AI Interpretation preview */}
                {signal.aiInterpretation && (
                  <div className="sig-card-preview">{signal.aiInterpretation}</div>
                )}

                {/* Expand hint */}
                <div className="sig-card-expand-hint">View full signal →</div>

                {/* Footer meta */}
                <div className="sig-card-footer">
                  {signal.source && (
                    <span className="sig-source">
                      {SOURCE_ICONS[signal.source] || '🔗'} {signal.source}
                    </span>
                  )}
                  {signal.dateObserved && (
                    <span className="sig-date">
                      {new Date((signal.dateObserved || '').slice(0, 10) + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                    </span>
                  )}
                  {signal.opportunityIndicators && signal.opportunityIndicators.length > 0 && (
                    <div className="sig-indicators">
                      {signal.opportunityIndicators.map(ind => (
                        <span
                          key={ind}
                          className="sig-indicator-badge"
                          style={{ background: OPP_INDICATOR_CONFIG[ind]?.bg, color: OPP_INDICATOR_CONFIG[ind]?.color }}
                        >
                          {ind}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
                {/* Selma feedback — did this signal lead somewhere? */}
                <div className="sig-card-feedback" onClick={e => e.stopPropagation()}>
                  {convertedSignals[signal.id] ? (
                    <span className="sig-converted-badge">✓ Led to conversation</span>
                  ) : (
                    <button
                      className="sig-convert-btn"
                      onClick={() => markConverted(signal.id, signal.name)}
                      type="button"
                      title="Mark this signal as having led to a real conversation or meeting"
                    >
                      💬 This led to a conversation
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Modal */}
      {(selectedSignal || modalMode === 'add') && (
        <div className="sig-modal-overlay" onClick={handleCloseModal}>
          <div className="sig-modal" onClick={e => e.stopPropagation()}>

            {/* Modal Header */}
            <div className="sig-modal-header">
              <h3>
                {modalMode === 'add'  ? '+ New Strategic Signal' :
                 modalMode === 'edit' ? 'Edit Signal' :
                 selectedSignal?.name}
              </h3>
              <button className="sig-close-btn" onClick={handleCloseModal}>×</button>
            </div>

            {/* Modal Body */}
            <div className="sig-modal-content">

              {/* VIEW MODE */}
              {modalMode === 'view' && selectedSignal && (
                <div className="sig-view">
                  {/* Type + Strength + Source row */}
                  <div className="sig-view-pills">
                    {selectedSignal.signalType && (() => {
                      const cfg = TYPE_CONFIG[selectedSignal.signalType];
                      return (
                        <span className="sig-view-pill" style={{ background: cfg?.bg, color: cfg?.color, borderColor: cfg?.color }}>
                          {cfg?.icon} {cfg?.label}
                        </span>
                      );
                    })()}
                    {selectedSignal.signalStrength && (() => {
                      const cfg = STRENGTH_CONFIG[selectedSignal.signalStrength];
                      return (
                        <span className="sig-view-pill" style={{ background: cfg?.bg, color: cfg?.color }}>
                          {cfg?.icon} {cfg?.label}
                        </span>
                      );
                    })()}
                    {selectedSignal.source && (
                      <span className="sig-source-pill">
                        {SOURCE_ICONS[selectedSignal.source]} {selectedSignal.source}
                      </span>
                    )}
                  </div>

                  {/* Date + Source Link */}
                  <div className="sig-view-meta">
                    {selectedSignal.dateObserved && (
                      <div className="sig-view-field">
                        <label>Signal Date</label>
                        <div className="sig-view-value">
                          {new Date((selectedSignal.dateObserved || '').slice(0, 10) + 'T12:00:00').toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                        </div>
                      </div>
                    )}
                    {selectedSignal.sourceLink && (
                      <div className="sig-view-field">
                        <label>Source Link</label>
                        <a href={selectedSignal.sourceLink} target="_blank" rel="noreferrer" className="sig-source-link">
                          🔗 View Source
                        </a>
                      </div>
                    )}
                  </div>

                  {/* Opportunity Indicators */}
                  {selectedSignal.opportunityIndicators && selectedSignal.opportunityIndicators.length > 0 && (
                    <div className="sig-view-section">
                      <label>Opportunity Indicators</label>
                      <div className="sig-indicators-row">
                        {selectedSignal.opportunityIndicators.map(ind => (
                          <span
                            key={ind}
                            className="sig-indicator-badge-lg"
                            style={{ background: OPP_INDICATOR_CONFIG[ind]?.bg, color: OPP_INDICATOR_CONFIG[ind]?.color }}
                          >
                            {ind}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* AI Interpretation */}
                  {selectedSignal.aiInterpretation && (
                    <div className="sig-view-section">
                      <label>🤖 AI Interpretation</label>
                      <div className="sig-ai-text">{selectedSignal.aiInterpretation}</div>
                    </div>
                  )}
                </div>
              )}

              {/* ADD / EDIT FORM */}
              {(modalMode === 'add' || modalMode === 'edit') && (
                <div className="sig-form">

                  <div className="sig-form-group">
                    <label>Signal Title * <span style={{fontWeight:400, color:'var(--dm-grey-600)', fontSize:'12px'}}>Be specific — include person, role, and statement</span></label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={e => handleFormChange('name', e.target.value)}
                      placeholder="e.g., John Smith, CIO — states modernizing core system is a key 2026 objective"
                      className="sig-input"
                    />
                  </div>

                  <div className="sig-form-row">
                    <div className="sig-form-group">
                      <label>Signal Type</label>
                      <select value={formData.signalType} onChange={e => handleFormChange('signalType', e.target.value)} className="sig-select">
                        <option value="">- Select -</option>
                        {SIGNAL_TYPES.map(t => (
                          <option key={t} value={t}>{TYPE_CONFIG[t].icon} {TYPE_CONFIG[t].label}</option>
                        ))}
                      </select>
                    </div>
                    <div className="sig-form-group">
                      <label>Signal Strength</label>
                      <select value={formData.signalStrength} onChange={e => handleFormChange('signalStrength', e.target.value)} className="sig-select">
                        <option value="">- Select -</option>
                        <option value="Strong">🔥 Strong</option>
                        <option value="Moderate">⚡ Moderate</option>
                        <option value="Weak">💧 Weak</option>
                      </select>
                    </div>
                  </div>

                  <div className="sig-form-row">
                    <div className="sig-form-group">
                      <label>Signal Date <span style={{fontWeight:400, color:'var(--dm-grey-600)', fontSize:'12px'}}>When the event occurred</span></label>
                      <input
                        type="date"
                        value={formData.signalDate}
                        onChange={e => handleFormChange('signalDate', e.target.value)}
                        className="sig-input"
                      />
                    </div>
                  </div>

                  <div className="sig-form-row">
                    <div className="sig-form-group">
                      <label>Source</label>
                      <select value={formData.source} onChange={e => handleFormChange('source', e.target.value)} className="sig-select">
                        <option value="">- Select -</option>
                        {Object.keys(SOURCE_ICONS).map(s => (
                          <option key={s} value={s}>{SOURCE_ICONS[s]} {s}</option>
                        ))}
                      </select>
                    </div>
                    <div className="sig-form-group">
                      <label>Source Link (URL)</label>
                      <input
                        type="url"
                        value={formData.sourceLink}
                        onChange={e => handleFormChange('sourceLink', e.target.value)}
                        placeholder="https://..."
                        className="sig-input"
                      />
                    </div>
                  </div>

                  <div className="sig-form-group">
                    <label>Opportunity Indicators</label>
                    <div className="sig-checkbox-group">
                      {['Integration', 'Core', 'AI', 'Data Gov'].map(ind => (
                        <label key={ind} className="sig-checkbox-label">
                          <input
                            type="checkbox"
                            checked={(formData.opportunityIndicators || []).includes(ind)}
                            onChange={() => handleIndicatorToggle(ind)}
                          />
                          <span
                            className="sig-ind-tag"
                            style={{
                              background: OPP_INDICATOR_CONFIG[ind]?.bg,
                              color: OPP_INDICATOR_CONFIG[ind]?.color,
                            }}
                          >
                            {ind}
                          </span>
                        </label>
                      ))}
                    </div>
                  </div>

                  <div className="sig-form-group">
                    <label>🤖 AI Interpretation</label>
                    <textarea
                      value={formData.aiInterpretation}
                      onChange={e => handleFormChange('aiInterpretation', e.target.value)}
                      placeholder="What does this signal mean for our opportunity? How should we act on it?"
                      className="sig-textarea"
                      rows={4}
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="sig-modal-footer">
              {modalMode === 'view' && (
                <>
                  <button className="sig-btn-delete" onClick={() => setShowDeleteConfirm(true)}>Delete</button>
                  <div className="sig-footer-right">
                    <button className="sig-btn-cancel" onClick={handleCloseModal}>Close</button>
                    <button className="sig-btn-edit" onClick={() => handleEditClick(selectedSignal)}>Edit</button>
                  </div>
                </>
              )}
              {(modalMode === 'add' || modalMode === 'edit') && (
                <>
                  {modalMode === 'add' && (
                    <label className="sig-add-another">
                      <input type="checkbox" checked={addAnother} onChange={e => setAddAnother(e.target.checked)} />
                      Create & Add Another
                    </label>
                  )}
                  <div className="sig-footer-right">
                    <button className="sig-btn-cancel" onClick={handleCloseModal}>Cancel</button>
                    <button
                      className="sig-btn-save"
                      onClick={handleSave}
                      disabled={saving || !formData.name.trim()}
                    >
                      {saving ? 'Saving...' : modalMode === 'add' ? 'Add Signal' : 'Save Changes'}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirm */}
      {showDeleteConfirm && (
        <div className="sig-modal-overlay" onClick={() => setShowDeleteConfirm(false)}>
          <div className="sig-delete-dialog" onClick={e => e.stopPropagation()}>
            <h3>Delete Signal?</h3>
            <p>Are you sure you want to delete <strong>"{selectedSignal?.name}"</strong>? This cannot be undone.</p>
            <div className="sig-delete-actions">
              <button className="sig-btn-cancel" onClick={() => setShowDeleteConfirm(false)}>Cancel</button>
              <button className="sig-btn-delete-confirm" onClick={handleDelete} disabled={deleting}>
                {deleting ? 'Deleting...' : 'Yes, Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default StrategicSignalsTab;
